import {
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddCircleOutline } from "@mui/icons-material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  addDefinition,
  getDefinitions,
  updateDefinition,
} from "../services/settingsService";
import { setDefinitions } from "../features/settingsSlice";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "select", label: "Dropdown" },
];

// Mirrors the server's deriveInternalName so the dialog can preview it live.
const deriveInternalName = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^([0-9])/, "f$1")
    .slice(0, 60);

const CustomFieldsSettings = () => {
  const settings = useSelector((state) => state.settings.value);
  const dispatch = useDispatch();
  const [entity, setEntity] = useState("property");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("text");
  const [optionsText, setOptionsText] = useState("");
  const [saving, setSaving] = useState(false);

  const fields = entity === "property" ? settings.propertyFields : settings.contactFields;
  const internalName = useMemo(() => deriveInternalName(label), [label]);

  const refresh = async () => {
    const defs = await getDefinitions();
    if (defs) {
      dispatch(setDefinitions(defs));
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setLabel("");
    setFieldType("text");
    setOptionsText("");
  };

  const onAdd = async () => {
    setSaving(true);
    try {
      const created = await addDefinition({
        entity,
        label,
        fieldType,
        options:
          fieldType === "select"
            ? optionsText.split(",").map((o) => o.trim()).filter(Boolean)
            : [],
      });
      if (created) {
        closeDialog();
        await refresh();
      }
    } finally {
      setSaving(false);
    }
  };

  const onToggleArchived = async (def) => {
    const updated = await updateDefinition(def.id, { archived: !def.archived });
    if (updated) {
      await refresh();
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack spacing={0.5}>
              <Typography variant="h6">Custom Fields</Typography>
              <Typography variant="body2" color="text.secondary">
                Fields appear in the property/contact editors, as table columns,
                and in the filter panel. CSV imports match columns by the label
                or the internal name.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={entity}
                onChange={(e, next) => next && setEntity(next)}
              >
                <ToggleButton value="property">Properties</ToggleButton>
                <ToggleButton value="contact">Contacts</ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                startIcon={<AddCircleOutline />}
                onClick={() => setDialogOpen(true)}
              >
                Add Field
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Label</TableCell>
                <TableCell>Internal Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Options</TableCell>
                <TableCell align="right">Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No custom {entity} fields yet. Add one to start capturing
                      client-specific data.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                fields.map((def) => (
                  <TableRow key={def.id} sx={{ opacity: def.archived ? 0.5 : 1 }}>
                    <TableCell>{def.label}</TableCell>
                    <TableCell>
                      <Chip size="small" label={def.internal_name} sx={{ fontFamily: "monospace" }} />
                    </TableCell>
                    <TableCell>{def.field_type}</TableCell>
                    <TableCell>{def.options?.join(", ") || "—"}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={def.archived ? "Unarchive" : "Archive (hides the field; stored values are kept)"}>
                        <Switch
                          size="small"
                          checked={!def.archived}
                          onChange={() => onToggleArchived(def)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          Add {entity === "property" ? "Property" : "Contact"} Field
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Fourth Cell Phone Number"
              fullWidth
              autoFocus
            />
            <TextField
              label="Internal Name"
              value={internalName}
              placeholder="fourth_cell_phone_number"
              helperText="Derived from the label. CSV columns matching either one import into this field. Cannot be changed later."
              fullWidth
              disabled
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value)}
              >
                {FIELD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {fieldType === "select" ? (
              <TextField
                label="Options (comma-separated)"
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder="Exit 333, Exit 336, Both"
                fullWidth
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onAdd}
            disabled={saving || !label.trim() || !internalName}
          >
            Add Field
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CustomFieldsSettings;
